import Link from 'next/link';

import { BlogAuthorSection } from '@/modules/blog/components/BlogAuthorSection';
import BlogPageDataLayerSessionStorage from '@/modules/blog/components/BlogPageDataLayerSessionStorage';
import { BlogPostCard } from '@/modules/blog/components/BlogPostCard';
import { BlogRecommendationProducts } from '@/modules/blog/components/BlogRecommendationProducts';
import { BlogProduct } from '@/modules/blog/model/blog-product.interface';
import { CtfRichText } from '@/modules/common/components/CtfRichText';
import Image from '@/modules/common/components/Image';
import { AlgoliaProvider } from '@/modules/product/components/AlgoliaSearch/AlgoliaProvider';

import { BlogPostFieldsFragment } from '@/gql/__generated/graphql.types';

import BlogPostDate from '../components/BlogPostDate';
import BlogPostLinks from '../components/BlogPostLinks';

type BlogPageTemplateProps = {
  blog: BlogPostFieldsFragment;
};

export const BlogPageTemplate = ({ blog }: BlogPageTemplateProps) => (
  <>
    <BlogPageDataLayerSessionStorage blog={blog} />

    <main className="mx-auto max-w-[950px] lg:px-8 lg:py-10">
      {blog.image && (
        <Image
          className="hidden w-full rounded-xl object-contain lg:block"
          src={blog.image?.url ?? ''}
          alt={blog.image.title ?? ''}
        />
      )}
      {blog.mobileImage && (
        <Image
          className="block w-full object-contain lg:hidden"
          src={blog.mobileImage?.url ?? ''}
          alt={blog.mobileImage.title ?? ''}
        />
      )}

      <header className="border-b border-gray-75 px-4 pb-7 pt-6 lg:px-10 lg:pt-16">
        <h1 className="mb-3 text-3xl font-bold lg:text-6xl">{blog?.title}</h1>
        <h2 className="mb-7 text-2xl font-bold text-gray-450">{blog?.subtitle}</h2>

        <p className="mb-7 text-gray-450">
          <BlogPostDate publicationDate={blog?.publicationDate} /> - by{' '}
          <Link
            href={`/blog/author/${blog.author?.slug}`}
            className="font-bold text-black underline"
          >
            {blog.author?.name}
          </Link>
        </p>

        <div className="flex gap-1.5">
          {blog.tags?.map((tag) => (
            <div key={tag} className="rounded-3xl border border-gray-75 px-2 py-0.5 text-xs">
              {tag}
            </div>
          ))}
        </div>
      </header>

      <section className="px-4 lg:px-8">
        <CtfRichText json={blog.blogContent?.json} links={blog.blogContent?.links} />
      </section>

      <AlgoliaProvider>
        <section className="px-4 lg:px-10">
          <BlogRecommendationProducts
            title={`${blog?.relatedProducts?.length > 1 ? 'Some ideas b' : 'B'}ased on the article`}
            blogSlug={blog.slug}
            blogTitle={blog.title}
            blogProducts={blog.relatedProducts as BlogProduct[]}
          />

          <BlogPostLinks shopLinks={blog.shopLinks} />
        </section>
      </AlgoliaProvider>

      <section className="border-75 mt-9 border-t px-4 py-8">
        <h2 className="font-bold">{blog.moreInformationTitle}</h2>
        <CtfRichText
          json={blog.moreInformationContent?.json}
          links={blog.moreInformationContent?.links}
        />
      </section>

      {blog.author && blog.displayMeetTheAuthorSection && (
        <div className="mb-12 mt-16">
          <BlogAuthorSection author={blog.author} />
        </div>
      )}
    </main>
    <AlgoliaProvider>
      {(blog?.relatedBlogPostsCollection?.items?.length || 0) > 0 && (
        <section>
          <h2
            className={
              'px-3 pb-9 pt-2 text-2xl font-bold leading-6 lg:px-5 lg:pb-6 lg:text-[32px] lg:leading-8'
            }
          >
            You may also like
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {blog?.relatedBlogPostsCollection?.items?.map((blog) => (
              <BlogPostCard key={blog?.slug} blog={blog} />
            ))}
          </div>
        </section>
      )}

      {(blog?.readyToShopProducts?.length || 0) > 0 ?
        <div className="mx-2 mt-5">
          <BlogRecommendationProducts
            title="Ready to shop?"
            blogProducts={blog.readyToShopProducts as BlogProduct[]}
            itemListId={`${blog.slug}-ready-to-shop`}
            itemListName={`${blog.title} - Ready to shop`}
            itemListType="Blog ready to shop"
          />
        </div>
      : null}
    </AlgoliaProvider>
  </>
);
